import * as React from "react";
import { RichTreeView, TreeItem2Props, TreeItem2, TreeItem2IconContainer } from "@mui/x-tree-view";
import caretRightSvg from "./images/caret-circle-right.svg?react";
import caretDownSvg from "./images/caret-circle-down.svg?react";
import NoResultImage from "../table/images/table-no-results-available.svg";
import styles from "./tree.module.css";

type CoreTreeViewProps<D extends object> = {
	disableSelection?: boolean;
	items: D[];
	expandIcon?: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & {
			title?: string;
		}
	>;
	collapseIcon?: React.FunctionComponent<
		React.SVGProps<SVGSVGElement> & {
			title?: string;
		}
	>;
	label: React.ElementType;
	getItemId?: (item: D) => string;
	getItemLabel?: (item: D) => string;
	noResultString?: string;
	noResultIcon?: string;
	/** If `true`, every item starts expanded, and re-expands whenever the item set changes. @default false */
	defaultExpandAll?: boolean;
};

function collectItemIds<D extends object>(items: D[], getItemId?: (item: D) => string): string[] {
	const ids: string[] = [];
	const visit = (nodes: D[]) => {
		nodes.forEach((node) => {
			// `getItemId` is optional on the tree view, which falls back to the item's `id`.
			const id = getItemId ? getItemId(node) : String((node as { id?: string | number }).id ?? "");
			if (id) ids.push(id);
			const children = (node as { children?: D[] }).children;
			if (children?.length) visit(children);
		});
	};
	visit(items);
	return ids;
}

type TreeItemIconContainerProps = React.ComponentPropsWithoutRef<typeof TreeItem2IconContainer>;
type CustomTreeItemProps = TreeItem2Props & { labelComponent: React.ElementType };

const CustomIconContainer = React.forwardRef<HTMLDivElement, TreeItemIconContainerProps>(
	function CustomIconContainerComponent(props, ref) {
		return (
			<TreeItem2IconContainer
				{...props}
				ref={ref}
				sx={{
					width: "20px",
				}}
			/>
		);
	},
);

const CustomTreeItem = React.forwardRef<HTMLLIElement, CustomTreeItemProps>(function CustomTreeItemComponent(
	props,
	ref,
) {
	const { labelComponent: LabelComponent, ...restProps } = props;
	return (
		<TreeItem2
			ref={ref}
			style={{
				fontSize: "14px",
			}}
			{...restProps}
			slots={{
				label: LabelComponent,
				iconContainer: CustomIconContainer,
			}}
			slotProps={{
				label: {
					id: `${props.itemId}`,
				},
			}}
		/>
	);
});

const treeViewSx = {
	"& .MuiTreeItem-content": {
		borderRadius: "50px",
		"&.Mui-selected": {
			backgroundColor: "#d5effd",
		},
		"&.Mui-focused": {
			backgroundColor: "transparent",
			outline: "2px solid var(--focus-outline-color-dark)",
			borderRadius: "2px",
		},
		"&:hover": {
			backgroundColor: "#f7f7f7",
		},
	},
	"& .MuiCollapse-root": {
		"--TreeView-itemChildrenIndentation": "25px", // Increase left padding for children
	},
};

export function CoreTreeView<D extends object>(props: CoreTreeViewProps<D>) {
	const ItemComponent = React.useMemo(
		() =>
			React.forwardRef<HTMLLIElement, TreeItem2Props>(function CoreTreeViewItemComponent(itemProps, ref) {
				return <CustomTreeItem {...itemProps} ref={ref} labelComponent={props.label} />;
			}),
		[props.label],
	);
	const slots = React.useMemo(
		() => ({
			expandIcon: props.expandIcon ?? caretRightSvg,
			collapseIcon: props.collapseIcon ?? caretDownSvg,
			item: ItemComponent,
		}),
		[ItemComponent, props.collapseIcon, props.expandIcon],
	);

	const allItemIds = React.useMemo(
		() => (props.defaultExpandAll ? collectItemIds(props.items, props.getItemId) : []),
		[props.defaultExpandAll, props.items, props.getItemId],
	);
	// Stringified rather than joined so ids containing the separator cannot produce the same key
	// for different item sets, which would skip the re-expand below.
	const allItemIdsKey = JSON.stringify(allItemIds);
	const [expandedItems, setExpandedItems] = React.useState(allItemIds);

	React.useEffect(() => {
		setExpandedItems(allItemIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allItemIdsKey]);

	return (
		<>
			{props.items.length > 0 && (
				<RichTreeView
					items={props.items}
					disableSelection={props.disableSelection}
					slots={slots}
					getItemId={props.getItemId}
					getItemLabel={props.getItemLabel}
					sx={treeViewSx}
					{...(props.defaultExpandAll && {
						expandedItems,
						onExpandedItemsChange: (_event: React.SyntheticEvent, itemIds: string[]) => setExpandedItems(itemIds),
					})}
				/>
			)}
			{props.items.length === 0 && (
				<div className={styles["no-results"]}>
					<img
						aria-label="No Search Result Image"
						src={props.noResultIcon ?? NoResultImage}
						className={styles["no-results-img"]}
					/>
					<p className={styles["no-results-p"]}>{props.noResultString}</p>
				</div>
			)}
		</>
	);
}
